import { Box, Grid, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import React, { useState, useEffect } from "react";
import { getActiveProjects } from "../../api/projectApi";
import { getUserProject } from "../../api/userApi";
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";
import { getStaffCalendar } from "../../api/employeeCalendarApi";
import { Project } from "../../types/projectTypes";
import { employeeCalendary } from "../../types/employeeCalendaryTypes";
import { getProjectActivities } from "../../api/activityApi";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import logo from "../../assets/images/logoElecnor.png";


const EmployeeCalendar: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<number>();
    const [calendarData, setCalendarData] = useState<employeeCalendary[]>([]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [filterTrigger, setFilterTrigger] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userRole, setUserRole] = useState<string>("");
    const [tableData, setTableData] = useState<Record<string, Record<string, string>>>({});
    const [employees, setEmployees] = useState<string[]>([]);
    const [dates, setDates] = useState<string[]>([]);

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        fetchCalendarData();
    }, [filterTrigger]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const userStr = localStorage.getItem("user");
            let role = "";
            let rut = "";
            if (userStr) {
                const user = JSON.parse(userStr);
                role = user.rols?.description;
                rut = user.identificationNum;
                setUserRole(role);
            }

            let projectsData: Project[] = [];

            // Lógica de roles: Director o Gerente ven todos los activos
            if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name || role === ADMIN_SISTEMA) {
                projectsData = await getActiveProjects();
            } else {
                // Otros roles ven solo los asignados
                const userProjects = await getUserProject(rut);
                projectsData = Array.isArray(userProjects) ? userProjects : [];
            }

            // Filtrar borrados lógicos
            const validProjects = projectsData.filter((p) => p.status === undefined || p.status === true);
            setProjects(validProjects);

        } catch (error) {
            console.error("Error al obtener proyectos:", error);
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

const formatDateDisplay = (dateStr: string): string => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
};

    const handleProjectChange = (event: SelectChangeEvent<number>) => {
        setSelectedProject(Number(event.target.value));
    };

    const formatDateTime = (dateString: string): string => {
        if (!dateString) return "";
        return `${dateString}T00:00:00`;
    };

    const fetchCalendarData = async () => {
        if (!selectedProject) return;

        try {
             setLoading(true);

             const activitiesMaster = await getProjectActivities(selectedProject);
             const validCips = activitiesMaster
                    .filter(item => item.activity.addProduction || item.activity.cip === "LT8.10.3")
                    .map(item => item.activity.cip);

                const formattedStartDate = formatDateTime(startDate);
                const formattedEndDate = formatDateTime(endDate);

                const data: employeeCalendary[] = await getStaffCalendar(formattedStartDate, formattedEndDate, selectedProject);

                const filteredData = data.filter(entry => {
                    if (!entry.cip) return false;
                    return validCips.includes(entry.cip);
                });

                const uniqueDates = Array.from(new Set(filteredData.map(entry => entry.activityDate.split("T")[0]))).sort();

                const uniqueEmployees = Array.from(
                    new Set(filteredData.map(entry => `${entry.fullName} - ${entry.rut}`))
                );

                const structuredData: Record<string, Record<string, string>> = {};
                uniqueDates.forEach(date => {
                    structuredData[date] = {};
                    uniqueEmployees.forEach(empIdentifier => {
                        // Buscamos la entrada que coincida con la fecha y el identificador combinado
                        const entry = filteredData.find(e =>
                            e.activityDate.startsWith(date) &&
                            `${e.fullName} - ${e.rut}` === empIdentifier
                        );
                        structuredData[date][empIdentifier] = entry ? entry.cip : "-";
                    });
                });

                setCalendarData(filteredData);
                setDates(uniqueDates);
                setEmployees(uniqueEmployees);
                setTableData(structuredData);
            } catch (error) {
                console.error("Error al obtener datos del calendario:", error);
            } finally {
                setLoading(false);
            }
        };

    const exportToExcel = async () => {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Calendario de Empleados");

            const imageResponse = await fetch(logo);
            const imageBuffer = await imageResponse.arrayBuffer();
            const imageId = workbook.addImage({
                buffer: imageBuffer,
                extension: "png",
            });

            // Ajustamos la imagen (opcionalmente puedes hacerlo más ancho si quieres)
            worksheet.addImage(imageId, {
                tl: { col: 0, row: 0 },
                ext: { width: 160, height: 60 },
            });

            worksheet.addRow([]);
            worksheet.addRow([]);
            worksheet.addRow([]);
            worksheet.addRow([]);

            // CAMBIO 1: Encabezados separados
            const headerRow = ["Nombre", "RUT", ...dates.map(date => formatDateDisplay(date))];
            const headerRowExcel = worksheet.addRow(headerRow);

            headerRowExcel.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: "FFFFFF" } };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "0070C0" },
                };
                cell.alignment = { horizontal: "center", vertical: "middle" };
                cell.border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" },
                };
            });

            employees.forEach((employeeKey, rowIndex) => {
                const separatorIndex = employeeKey.lastIndexOf(" - ");
                const name = employeeKey.substring(0, separatorIndex);
                const rut = employeeKey.substring(separatorIndex + 3);

                const rowData = [name, rut, ...dates.map(date => tableData[date]?.[employeeKey] || "-")];
                const rowExcel = worksheet.addRow(rowData);

                rowExcel.eachCell((cell, colIndex) => {
                    cell.border = {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                        left: { style: "thin" },
                        right: { style: "thin" },
                    };

                    if (rowIndex % 2 === 0) {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "DCE6F1" },
                        };
                    }

                    // CAMBIO 3: Ahora las fechas empiezan después de la columna 2
                    if (colIndex > 2) {
                        cell.alignment = { horizontal: "center" };
                    }
                });
            });

            // Ajuste de anchos de columna
            worksheet.getColumn(1).width = 30; // Ancho para Nombre
            worksheet.getColumn(2).width = 15; // Ancho para RUT

            // Ajustar ancho de columnas de fecha
            for (let i = 3; i <= headerRow.length; i++) {
                 worksheet.getColumn(i).width = 12;
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            saveAs(blob, `Calendario_Empleados.xlsx`);
        };

    return (
        <Box sx={{ p: 3 }}>
            {loading && <CircularProgress />}

            {/* Filtro de Fechas */}
            <Grid container spacing={2} sx={{ marginBottom: 1 }}>
                <Grid item xs={4}>
                    <FormControl fullWidth sx={{ marginBottom: 3 }}>
                        <InputLabel>Seleccionar Proyecto</InputLabel>
                        <Select
                            label="Seleccionar Proyecto"
                            value={selectedProject || ""}
                            onChange={handleProjectChange}
                        >
                            {projects.map((project) => (
                                <MenuItem key={project.id} value={project.id}>
                                    {project.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        label="Fecha de Inicio"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        label="Fecha de Término"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setFilterTrigger(prev => !prev)}
                >
                    Filtrar
                </Button>
            </Box>

            {/* Tabla de Calendario */}
       {/* Tabla de Calendario */}
                   <TableContainer component={Paper} sx={{ mt: 3, margin: "auto", maxWidth: "100%", overflow: "auto", maxHeight: "calc(100vh - 300px)", boxShadow: 3 }}>
                       <Table size="small" stickyHeader>
                           <TableHead>
                               <TableRow sx={{ height: "32px" }}>
                                   {/* COLUMNA 1: NOMBRE */}
                                   <TableCell
                                       sx={{
                                           fontSize: "0.8rem",
                                           padding: "4px",
                                           fontWeight: "bold",
                                           position: "sticky",
                                           left: 0,
                                           top: 0,
                                           backgroundColor: "#78a1bf",
                                           color: "#004F86",
                                           borderRight: "1px solid #ccc",
                                           zIndex: 5,
                                           whiteSpace: "nowrap",
                                           minWidth: "200px", // Ancho fijo necesario para sticky sucesivo
                                           maxWidth: "200px"
                                       }}
                                   >
                                       <strong>Nombre</strong>
                                   </TableCell>

                                   <TableCell
                                       sx={{
                                           fontSize: "0.8rem",
                                           padding: "4px",
                                           fontWeight: "bold",
                                           position: "sticky",
                                           left: "200px", // Debe coincidir con el ancho de la columna anterior
                                           top: 0,
                                           backgroundColor: "#78a1bf",
                                           color: "#004F86",
                                           borderRight: "1px solid #ccc",
                                           zIndex: 5,
                                           whiteSpace: "nowrap",
                                           minWidth: "120px",
                                           textAlign: "center"
                                       }}
                                   >
                                       <strong>RUT</strong>
                                   </TableCell>

                                   {dates.map((date) => (
                                       <TableCell
                                           key={date}
                                           align="center"
                                           sx={{
                                               fontSize: "0.8rem",
                                               padding: "4px",
                                               fontWeight: "bold",
                                               border: "1px solid #ccc",
                                               textAlign: "center",
                                               backgroundColor: "#78a1bf",
                                               color: "#004F86",
                                               top: 0,
                                               zIndex: 4,
                                               minWidth: "80px"
                                           }}
                                       >
                                           <strong>{formatDateDisplay(date)}</strong>
                                       </TableCell>
                                   ))}
                               </TableRow>
                           </TableHead>
                           <TableBody>
                               {employees.map((employeeKey) => {
                                   // Lógica de separación segura
                                   const separatorIndex = employeeKey.lastIndexOf(" - ");
                                   const name = employeeKey.substring(0, separatorIndex);
                                   const rut = employeeKey.substring(separatorIndex + 3);

                                   return (
                                       <TableRow key={employeeKey} sx={{ height: "32px" }}>
                                           {/* CELDA NOMBRE */}
                                           <TableCell
                                               sx={{
                                                   fontSize: "0.8rem",
                                                   padding: "4px",
                                                   fontWeight: "bold",
                                                   position: "sticky",
                                                   left: 0,
                                                   backgroundColor: "#f3f3f3",
                                                   borderRight: "1px solid #ccc",
                                                   zIndex: 2,
                                                   whiteSpace: "nowrap",
                                                   minWidth: "200px",
                                                   maxWidth: "200px",
                                                   overflow: "hidden",
                                                   textOverflow: "ellipsis"
                                               }}
                                               title={name} // Tooltip por si el nombre es muy largo
                                           >
                                               {name}
                                           </TableCell>

                                           {/* CELDA RUT */}
                                           <TableCell
                                               sx={{
                                                   fontSize: "0.8rem",
                                                   padding: "4px",
                                                   fontWeight: "bold",
                                                   position: "sticky",
                                                   left: "200px", // Mismo valor que en el Header
                                                   backgroundColor: "#f3f3f3",
                                                   borderRight: "1px solid #ccc",
                                                   zIndex: 2,
                                                   whiteSpace: "nowrap",
                                                   minWidth: "120px",
                                                   textAlign: "center"
                                               }}
                                           >
                                               {rut}
                                           </TableCell>

                                           {dates.map((date) => (
                                               <TableCell key={date} align="center" sx={{ fontSize: "0.8rem", padding: "4px" }}>
                                                   {tableData[date]?.[employeeKey] || "-"}
                                               </TableCell>
                                           ))}
                                       </TableRow>
                                   );
                               })}
                           </TableBody>
                       </Table>
                   </TableContainer>

            <Box display="flex" justifyContent="flex-end" marginTop={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => exportToExcel()}
                    sx={{ mt: 2 }}
                    disabled={userRole === profiles[3].name}
                >
                    Descargar a Excel
                </Button>
            </Box>
        </Box>
    );
};

export default EmployeeCalendar;